import React, { useState, useEffect } from "react";
import { Form, Input, Select, Button, Upload, Card, message } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import FeatherIcon from "feather-icons-react";
import { X } from "react-feather";
import {
  createMarketingFile,
  getMarketingFileById,
  getMarketingFileTypes,
  updateMarketingFile,
} from "../../core/api/marketing";
import { useSelector } from "react-redux";
import { fetchBrandsBySelectedClients } from "../../core/api/settings";

const { Option } = Select;

const AddEnvelope = ({
  isVisible,
  onClose,
  onResize,
  clientId,
  envelopeId,
}) => {
  const [form] = Form.useForm();
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [fileTypes, setFileTypes] = useState([]);
  const [fileList, setFileList] = useState([]);
  const [brands, setBrands] = useState([]);

  useEffect(() => {
    const fetchBrands = async () => {
      if (!clientId) return;
      try {
        const response = await fetchBrandsBySelectedClients([clientId]);
        setBrands(response.data);
      } catch (error) {
        message.error("Failed to fetch brands.");
      }
    };
    fetchBrands();
  }, [clientId]);

  useEffect(() => {
    const fetchFileTypes = async () => {
      try {
        const response = await getMarketingFileTypes();
        setFileTypes(response.data);
      } catch (error) {
        message.error("Failed to fetch file types.");
      }
    };
    fetchFileTypes();
  }, []);

  useEffect(() => {
    const fetchEnvelopeData = async () => {
      if (!envelopeId) return;
      try {
        const data = (await getMarketingFileById(envelopeId)).data;
        setFileList([
          {
            uid: "-1",
            name: data.file_name,
            status: "done",
            url: data.absolute_url,
          },
        ]);
        form.setFieldsValue({
          file_code: data.file_code,
          type_id: data.type?.id,
          file_name: data.file_name,
          number_of_pages: data.number_of_pages,
          length: data.length,
          width: data.width,
          brand: data.brand,
        });
      } catch (error) {
        console.error(error);
        message.error("Failed to fetch envelope data.");
      }
    };
    fetchEnvelopeData();
  }, [envelopeId, form]);

  useEffect(() => {
    const sidebarWidth = isFullScreen ? window.innerWidth * 0.6 : 555;
    onResize(sidebarWidth);
  }, [isFullScreen, onResize]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const formData = new FormData();
      Object.keys(values).forEach((key) => {
        if (key !== "file") formData.append(key, values[key]);
      });

      if (fileList.length > 0 && fileList[0]?.originFileObj) {
        formData.append("file", fileList[0].originFileObj);
      }

      if (envelopeId) {
        await updateMarketingFile(envelopeId, formData);
        message.success("Envelope updated successfully.");
      } else {
        await createMarketingFile(formData);
        message.success("Envelope added successfully.");
      }

      form.resetFields();
      setFileList([]);
      onClose();
    } catch (error) {
      console.error("Failed to submit envelope:", error);
      message.error("Failed to save envelope.");
    }
  };

  const data = useSelector((state) => state.toggle_header);

  return (
    <div
      className={`right-sidebar ${isVisible ? "visible" : ""}`}
      style={{
        position: "fixed",
        top: data ? 0 : 66,
        right: 0,
        height: "100%",
        zIndex: 1050,
        width: isFullScreen ? "60%" : "555px",
        backgroundColor: "#fff",
        boxShadow: "0 0 3px rgba(0, 0, 0, 0.6)",
        overflowY: "auto",
        transform: isVisible ? "translateX(0)" : "translateX(100%)",
        transition: "transform 0.3s ease-in-out",
      }}
    >
      <div
        className="sidebar-header"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "15px",
          borderBottom: "1px solid #ddd",
        }}
      >
        <h5>{envelopeId ? "Edit Envelope" : "Add Envelope"}</h5>
        <div>
          <button
            onClick={() => {
              setIsFullScreen(!isFullScreen);
            }}
            className="btn"
          >
            <FeatherIcon icon={isFullScreen ? "minimize" : "maximize"} />
          </button>

          <button
            className="btn"
            onClick={() => {
              onClose();
              form.resetFields();
              setFileList([]);
            }}
          >
            <X />
          </button>
        </div>
      </div>

      <div className="modal-body" style={{ padding: "15px" }}>
        <Form form={form} layout="vertical">
          <Card title="Envelope Details">
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "15px",
              }}
            >
              <Form.Item
                label="Brand"
                name="brand"
                rules={[{ required: true, message: "Please select a brand" }]}
              >
                <Select placeholder="Select Brand" allowClear>
                  {brands.map((brand) => (
                    <Option key={brand.id} value={brand.id}>
                      {brand.brand_name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                label="File Code"
                name="file_code"
                rules={[
                  { required: true, message: "Please enter a file code" },
                ]}
              >
                <Input placeholder="Enter File Code" />
              </Form.Item>

              <Form.Item
                label="File Type"
                name="type_id"
                rules={[
                  { required: true, message: "Please select a file type" },
                ]}
              >
                <Select placeholder="Select File Type" allowClear>
                  {fileTypes.map((type) => (
                    <Option key={type.id} value={type.id}>
                      {type.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                label="File Name"
                name="file_name"
                rules={[
                  { required: true, message: "Please enter a file name" },
                ]}
              >
                <Input placeholder="Enter File Name" />
              </Form.Item>

              <Form.Item
                label="Pages"
                name="number_of_pages"
                rules={[{ required: true, message: "Please enter pages" }]}
              >
                <Input placeholder="Enter Pages" />
              </Form.Item>

              <Form.Item
                label="Length"
                name="length"
                rules={[{ required: true, message: "Please enter length" }]}
              >
                <Input placeholder="Enter Length" />
              </Form.Item>

              <Form.Item
                label="Width"
                name="width"
                rules={[{ required: true, message: "Please enter width" }]}
              >
                <Input placeholder="Enter Width" />
              </Form.Item>

              <Form.Item
                label="File"
                name="file"
                valuePropName="file"
                getValueFromEvent={(e) => e?.fileList?.[0]}
                style={{ width: "250px" }}
              >
                <Upload
                  beforeUpload={() => false}
                  maxCount={1}
                  accept=".pdf"
                  fileList={fileList}
                  onChange={({ fileList: newFileList }) =>
                    setFileList(newFileList)
                  }
                >
                  <Button icon={<UploadOutlined />}>Upload File</Button>
                </Upload>
              </Form.Item>
            </div>
          </Card>

          <div style={{ textAlign: "right", marginTop: "20px" }}>
            <Button
              type="primary"
              className="btn btn-primary"
              onClick={handleSubmit}
            >
              {envelopeId ? "Update Envelope" : "Add Envelope"}
            </Button>
          </div>
        </Form>
      </div>
    </div>
  );
};

export default AddEnvelope;
